<template>
  <NuxtLink 
    :to="href"
    class="scifi-button"
    :class="[
      mappedButtonType,
      sizeClass,
      { active: isActive, 'full-borders': useFullBorders }
    ]"
  >
    <span class="button-text">
      <slot />
    </span>
    
    <!-- Code rain animation overlay -->
    <div class="code-rain"></div>
  </NuxtLink>
</template>

<script setup lang="ts">
interface Props {
  href: string
  variant?: 'primary' | 'secondary' | 'warning' | 'danger'
  size?: 'small' | 'medium' | 'large'
  isActive?: boolean
  useFullBorders?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'medium',
  isActive: false,
  useFullBorders: false
})

// Map variant to buttonType classes from original ScifiButton
const mappedButtonType = computed(() => {
  switch (props.variant) {
    case 'primary':
      return 'positive'
    case 'secondary':
      return 'normal'
    case 'warning':
      return 'warning'
    case 'danger':
      return 'negative'
    default:
      return 'normal'
  }
})

// Size classes
const sizeClass = computed(() => {
  switch (props.size) {
    case 'small':
      return 'size-small'
    case 'large':
      return 'size-large'
    case 'medium':
    default:
      return 'size-medium'
  }
})
</script>

<style scoped>
.scifi-button {
  display: inline-block;
  position: relative;
  font-size: 1em;
  line-height: 1em;
  text-transform: uppercase;
  background: transparent;
  padding: 0.75rem 1.2rem;
  text-decoration: none;
  color: #fff;
  margin: 0;
  border: 1px solid var(--ts-accessibility-border, #444);
  border-radius: 0;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.05, 3, 1, 1) 0s;
  font-family: 'OptimusPrinceps', sans-serif;
  font-weight: 600;
  box-sizing: border-box;
}

.button-text {
  position: relative;
  z-index: 2;
}

/* Size variations */
.size-small {
  padding: 0.5rem 0.8rem;
  font-size: 0.875em;
}

.size-medium {
  padding: 0.75rem 1.2rem;
  font-size: 1em;
}

.size-large {
  padding: 1rem 1.6rem;
  font-size: 1.125em;
}

/* Corner highlight pseudo-elements */
.scifi-button:after,
.scifi-button:before {
  content: "";
  display: block;
  position: absolute;
  width: 12px;
  height: 12px;
  border: 2px solid transparent;
  transition: all 0.6s cubic-bezier(0.05, 3, 1, 1) 0s;
  box-sizing: border-box;
  clip-path: inset(0);
}

.scifi-button:before {
  top: 0;
  left: 0;
  border-top: 2px solid transparent;
  border-left: 2px solid transparent;
  border-right: none;
  border-bottom: none;
}

.scifi-button:after {
  bottom: 0;
  right: 0;
  border-bottom: 2px solid transparent;
  border-right: 2px solid transparent;
  border-top: none;
  border-left: none;
}

/* Normal state */
.scifi-button.normal:hover:before {
  border-top-color: #1ae3de;
  border-left-color: #1ae3de;
}

.scifi-button.normal:hover:after {
  border-bottom-color: #1ae3de;
  border-right-color: #1ae3de;
}

/* Positive state (primary variant) */
.scifi-button.positive {
  border: 1px solid rgba(0, 255, 0, 0.3);
}

.scifi-button.positive:hover:before {
  border-top-color: #0f0;
  border-left-color: #0f0;
}

.scifi-button.positive:hover:after {
  border-bottom-color: #0f0;
  border-right-color: #0f0;
}

/* Negative state (danger variant) */
.scifi-button.negative {
  border: 1px solid rgba(255, 0, 0, 0.3);
}

.scifi-button.negative:hover:before {
  border-top-color: #f00;
  border-left-color: #f00;
}

.scifi-button.negative:hover:after {
  border-bottom-color: #f00;
  border-right-color: #f00;
}

/* Warning state */
.scifi-button.warning {
  border: 1px solid rgba(255, 165, 0, 0.3);
}

.scifi-button.warning:hover:before {
  border-top-color: #ff8800;
  border-left-color: #ff8800;
}

.scifi-button.warning:hover:after {
  border-bottom-color: #ff8800;
  border-right-color: #ff8800;
}

/* Active state - permanent green corner highlights only */
.scifi-button.active:before {
  border-top-color: #0f0;
  border-left-color: #0f0;
}

.scifi-button.active:after {
  border-bottom-color: #0f0;
  border-right-color: #0f0;
}

/* Main navigation - inactive state keeps corner highlights (like tab buttons) */
.scifi-button.full-borders.normal:not(.active)::before {
  width: 12px;
  height: 12px;
  top: 0;
  left: 0;
  border-top-color: #1ae3de;
  border-left-color: #1ae3de;
}

.scifi-button.full-borders.normal:not(.active)::after {
  width: 12px;
  height: 12px;
  bottom: 0;
  right: 0;
  border-bottom-color: #1ae3de;
  border-right-color: #1ae3de;
}

/* Main navigation - hover state with full bracket borders */
.scifi-button.full-borders.normal:not(.active):hover::before {
  width: 10px;
  height: calc(100% - 8px);
  max-height: calc(100% - 8px);
  top: 4px;
  left: 2px;
  border: 2px solid #0f0;
  border-right: none;
  clip-path: inset(0 0 0 0);
  transition: none; /* Instant appearance, no animation */
}

.scifi-button.full-borders.normal:not(.active):hover::after {
  width: 10px;
  height: calc(100% - 8px);
  max-height: calc(100% - 8px);
  top: 4px;
  right: 2px;
  border: 2px solid #0f0;
  border-left: none;
  clip-path: inset(0 0 0 0);
  transition: none; /* Instant appearance, no animation */
}

/* Main navigation - active state with full bracket borders */
.scifi-button.full-borders.active::before {
  width: 10px;
  height: calc(100% - 8px);
  top: 4px;
  left: 2px;
  border: 2px solid #0f0;
  border-right: none;
  transition: none; /* No animation for active state */
}

.scifi-button.full-borders.active::after {
  width: 10px;
  height: calc(100% - 8px);
  top: 4px;
  right: 2px;
  border: 2px solid #0f0;
  border-left: none;
  transition: none; /* No animation for active state */
}

/* Code rain animation */
.code-rain {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 255, 0, 0) 0%,
    rgba(0, 255, 0, 0.2) 50%,
    rgba(0, 255, 0, 0) 100%
  );
  opacity: 0;
  transition: 0.3s;
  z-index: 1;
}

.scifi-button:hover .code-rain {
  opacity: 1;
  animation: rain 1s linear infinite;
}

/* Rain animation for active state (always running but subtle) */
.scifi-button.active .code-rain {
  opacity: 0.5;
  animation: rain 2s linear infinite;
}

@keyframes rain {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}
</style>